import { useState } from "react";
import { KeyRound, ShieldCheck, Building2, CircleAlert } from "lucide-react";
import { Pagina, Cartao } from "../shell/Pagina.js";
import { useEu } from "../shell/Sessao.js";
import { MINIMO } from "@pessoas/domain/senha";

/** Meus dados. Leitura por enquanto: cadastro é do RH, não do próprio usuário. */
export function Conta() {
  const eu = useEu();
  return (
    <Pagina
      secao="Sua conta" titulo="Meus dados"
      descricao="Quem você é dentro do sistema e o que o seu papel permite fazer."
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao titulo="Identificação" subtitulo="O login é por CPF, não por e-mail">
          <dl className="eop-dados">
            <dt>Nome</dt><dd>{eu.nome ?? "sem registro"}</dd>
            <dt>CPF</dt>
            {/* Só os 4 últimos. O número inteiro na tela é dado pessoal exposto
                a quem passa atrás da cadeira. */}
            <dd className="num">{eu.cpfFinal ? `•••.•••.•••-${eu.cpfFinal.slice(-2)}` : "sem registro"}</dd>
            <dt>Papel</dt><dd>{eu.papel ?? "sem papel"}</dd>
          </dl>
          <p className="carimbo mt-4">
            para corrigir nome ou CPF, fale com o RH: cadastro de pessoa não se
            edita pelo próprio usuário
          </p>
        </Cartao>

        <Cartao titulo="Onde você tem acesso" subtitulo="Um CPF pode ter vínculo em mais de uma empresa">
          <ul className="eop-empresas-lista">
            {eu.empresas.map((e) => (
              <li key={e.companyId} data-atual={e.companyId === eu.empresaAtual || undefined}>
                <Building2 aria-hidden size={15} />
                <span className="min-w-0 flex-1 truncate">{e.nome}</span>
                <span className="pilula">{e.papel}</span>
              </li>
            ))}
          </ul>
        </Cartao>
      </div>

      <Cartao titulo="O que o seu papel permite" subtitulo={`${eu.permissoes.length} permissões nesta empresa`}>
        <ul className="eop-permissoes">
          {[...eu.permissoes].sort().map((p) => <li key={p} className="num">{p}</li>)}
        </ul>
      </Cartao>
    </Pagina>
  );
}

export function TrocarSenha() {
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [confirma, setConfirma] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // A confirmação é conferida NO CLIENTE, de propósito: mandar as duas ao
  // servidor para ele dizer "não bate" gasta uma ida para uma checagem que a
  // tela já consegue fazer, e o servidor nunca precisa ver a confirmação.
  const naoBate = confirma.length > 0 && nova !== confirma;
  const podeEnviar = atual.length > 0 && nova.length >= MINIMO && !naoBate && !enviando;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null); setEnviando(true);
    try {
      const r = await fetch("/auth/senha", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ atual, nova }),
      });
      const corpo = await r.json() as { erro?: string };
      if (!r.ok) { setErro(corpo.erro ?? "Não consegui trocar a senha."); return; }
      // Trocar a senha derruba TODAS as sessões, inclusive esta. Recarregar
      // leva para o login, que é o comportamento correto e esperado.
      window.location.assign("/");
    } catch {
      setErro("Não consegui falar com o servidor. Tente de novo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Pagina
      secao="Sua conta" titulo="Trocar a senha"
      descricao="Ao trocar, todas as sessões abertas são encerradas, inclusive esta e as de outros aparelhos."
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_1fr]">
        <Cartao titulo="Nova senha" subtitulo={`Mínimo de ${MINIMO} caracteres`}>
          <form onSubmit={(e) => void enviar(e)} className="space-y-3.5">
            <Campo rotulo="Senha atual" valor={atual} aoMudar={setAtual} autoComplete="current-password" />
            <Campo rotulo="Senha nova" valor={nova} aoMudar={setNova} autoComplete="new-password" />
            <Campo rotulo="Repita a senha nova" valor={confirma} aoMudar={setConfirma}
                   autoComplete="new-password" {...(naoBate ? { erro: "As duas não conferem." } : {})} />

            {erro && (
              <p className="eop-erro" role="alert">
                <CircleAlert aria-hidden size={15} />
                <span>{erro}</span>
              </p>
            )}

            <button type="submit" className="botao w-full" data-variante="primario" disabled={!podeEnviar}>
              <KeyRound aria-hidden size={15} />
              {enviando ? "Trocando" : "Trocar a senha"}
            </button>
          </form>
        </Cartao>

        <Cartao titulo="Como escolher" subtitulo="A regra vale aqui, no convite e na linha de comando">
          <ul className="eop-porque">
            <li><strong>Comprimento vence complexidade.</strong> Uma frase de {MINIMO} caracteres que só você saiba é mais forte que oito caracteres com símbolo no meio.</li>
            <li><strong>Sem o seu nome.</strong> Ele é público dentro da empresa, então é o primeiro palpite de quem tenta entrar.</li>
            <li><strong>Sem dígitos do seu CPF.</strong> É o mesmo número que você usa para entrar, e quem tem ele já tem metade.</li>
            <li><strong>Sem sequência.</strong> Repetição de letra e ordem de teclado são recusadas.</li>
          </ul>
        </Cartao>
      </div>
    </Pagina>
  );
}

function Campo(
  { rotulo, valor, aoMudar, autoComplete, erro }:
  { rotulo: string; valor: string; aoMudar: (v: string) => void;
    autoComplete: string; erro?: string },
) {
  return (
    <label className="block">
      <span className="eop-rotulo">{rotulo}</span>
      <input
        type="password" className="campo mt-1.5" value={valor} autoComplete={autoComplete}
        onChange={(e) => aoMudar(e.target.value)}
        aria-invalid={erro ? true : undefined}
      />
      {erro && <span className="eop-campo-erro">{erro}</span>}
    </label>
  );
}

export function Ajuda() {
  return (
    <Pagina secao="Sua conta" titulo="Ajuda e suporte"
            descricao="Onde pedir socorro, e o que ter em mãos ao abrir um chamado.">
      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao titulo="Ao relatar um problema" subtitulo="O que faz o chamado ser resolvido na primeira volta">
          <ul className="eop-porque">
            <li><strong>O horário.</strong> Com minuto, e o fuso se a filial não for a mesma do escritório.</li>
            <li><strong>A empresa e a competência.</strong> O mesmo cálculo em competências diferentes usa tabelas legais diferentes.</li>
            <li><strong>O caminho que você fez.</strong> A tela, o botão e o que apareceu, mesmo que pareça óbvio.</li>
          </ul>
        </Cartao>
        <Cartao titulo="Segurança da sua conta" subtitulo="O que o sistema faz por você">
          <ul className="eop-porque">
            <li><ShieldCheck aria-hidden size={14} style={{ display: "inline", marginRight: 6 }} />
              <strong>Trocar a senha encerra todas as sessões</strong>, inclusive as de outros aparelhos.</li>
            <li><strong>Tentativa repetida bloqueia o acesso</strong> por tempo, e a contagem é por CPF e por origem.</li>
            <li><strong>Cada leitura de dado sensível fica registrada</strong>, com quem, quando e de onde.</li>
          </ul>
        </Cartao>
      </div>
    </Pagina>
  );
}
